// Mate. The line is a forced mate (the engine scores it as one, or the fact
// names `mate` as the material). The pattern is read on the mated position
// when the line reaches it by the rules (a short mate; the scan keeps at
// most eight plies), from the mating piece, the king and what stands around
// it; a line cut short before the mate, or one with no named pattern, is
// `other`. The patterns, first match wins:
//
// - smothered: a knight mates a king whose every neighbouring square holds
//   one of its own pieces;
// - arabian: a rook mates a king in the corner from the square beside it,
//   defended by a knight;
// - hook: a rook mates from beside the king, defended by a knight that a
//   pawn defends;
// - corner: a knight mates a king in the corner (a rook or queen confines it);
// - anastasia: a rook or queen mates along the edge file (or rank) with a
//   knight on the king's rank (or file) three squares in, covering the
//   escape squares;
// - backRank: a rook or queen mates along the king's home rank, the squares
//   in front of the king blocked by its own pieces or covered;
// - doubleBishop and boden: a bishop mates with a second bishop covering an
//   escape square, their diagonals parallel (double bishop) or crossing
//   (Boden's);
// - dovetail: the queen mates from the square diagonally beside the king,
//   the king's two free neighbours blocked by its own pieces;
// - epaulette: the queen mates from two squares straight in front of the
//   king, the squares either side of the king blocked by its own pieces.
import type { Motif } from '@chess/api-mistakes';
import { positionAfter, type TacticLine } from './line.ts';
import { Board, distance, fileOf, homeRank, forward, kingSquare, neighbours, rankOf, squareAt, stepBetween, type Placed } from './rays.ts';

const CORNERS = ['a1', 'h1', 'a8', 'h8'];

function patternOf(board: Board, lastMove: string, side: 'white' | 'black'): string {
  const victim = side === 'white' ? 'black' : 'white';
  const king = kingSquare(board, victim);
  const to = lastMove.slice(2, 4);
  const mater = board.get(to);
  if (!king || !mater || mater.colour !== side) return 'other';
  const own = (sq: string) => board.get(sq)?.colour === victim;
  const covered = (sq: string) => own(sq) || board.attackers(sq, side).length > 0;
  const around = neighbours(king);
  const defenders = (sq: string): Placed[] => board.attackers(sq, side).map((square) => ({ square, ...board.get(square)! }));
  const beside = distance(to, king) === 1 && (fileOf(to) === fileOf(king) || rankOf(to) === rankOf(king));
  const inCorner = CORNERS.includes(king);

  if (mater.kind === 'knight' && around.every(own)) return 'smothered';
  if (mater.kind === 'rook' && beside) {
    const knight = defenders(to).find((d) => d.kind === 'knight');
    if (knight && inCorner) return 'arabian';
    if (knight && defenders(knight.square).some((d) => d.kind === 'pawn')) return 'hook';
  }
  if (mater.kind === 'knight' && inCorner) return 'corner';
  if (mater.kind === 'rook' || mater.kind === 'queen') {
    const kf = fileOf(king);
    const kr = rankOf(king);
    const knights = board.pieces().filter((p) => p.colour === side && p.kind === 'knight');
    if ((kf === 0 || kf === 7) && fileOf(to) === kf && knights.some((n) => rankOf(n.square) === kr && Math.abs(fileOf(n.square) - kf) === 3)) return 'anastasia';
    if ((kr === 0 || kr === 7) && rankOf(to) === kr && knights.some((n) => fileOf(n.square) === kf && Math.abs(rankOf(n.square) - kr) === 3)) return 'anastasia';
    if (kr === homeRank(victim) && rankOf(to) === kr) {
      const front = [-1, 0, 1].map((df) => squareAt(kf + df, kr + forward(victim))).filter((s): s is string => !!s);
      if (front.every(covered)) return 'backRank';
    }
  }
  if (mater.kind === 'bishop') {
    const step = stepBetween(to, king);
    const other = board.pieces().find((p) => p.colour === side && p.kind === 'bishop' && p.square !== to && board.attacksFrom(p.square).some((sq) => around.includes(sq)));
    if (step && other) {
      const escape = board.attacksFrom(other.square).find((sq) => around.includes(sq))!;
      const step2 = stepBetween(other.square, escape);
      if (step2) return step[0] * step[1] === step2[0] * step2[1] ? 'doubleBishop' : 'boden';
    }
  }
  if (mater.kind === 'queen') {
    const step = stepBetween(to, king);
    const d = distance(to, king);
    if (step && d === 1 && step[0] !== 0 && step[1] !== 0) {
      const free = around.filter((sq) => sq !== to && !board.attacksFrom(to).includes(sq) && stepBetween(to, sq)?.join() !== step.join());
      if (free.length === 2 && free.every(own)) return 'dovetail';
    }
    if (step && d === 2 && (step[0] === 0 || step[1] === 0)) {
      const sides = step[0] === 0 ? [squareAt(fileOf(king) - 1, rankOf(king)), squareAt(fileOf(king) + 1, rankOf(king))] : [squareAt(fileOf(king), rankOf(king) - 1), squareAt(fileOf(king), rankOf(king) + 1)];
      if (sides.every((sq) => !!sq && own(sq))) return 'epaulette';
    }
  }
  return 'other';
}

export function mate(line: TacticLine): Motif | undefined {
  const forced = (line.mate ?? 0) > 0 || line.material === 'mate';
  if (!forced) return undefined;
  const plies = line.pv.length;
  const end = plies ? positionAfter(line, plies) : undefined;
  if (!end || !line.chess.position(end).checkmate) return { family: 'mate', variation: 'other' };
  return { family: 'mate', variation: patternOf(Board.fromFen(end), line.pv[plies - 1], line.side) };
}
